import ethereumBareStrategies from '@/configs/ethereum-bare-strategies.json';
import ethereumArchiveStrategies from '@/configs/ethereum-archive-strategies.json';
import polygonBareStrategies from '@/configs/polygon-bare-strategies.json';
import arbitrumArchiveStrategies from '@/configs/arbitrum-archive-strategies.json';
import moonbeamArchiveStrategies from '@/configs/moonbeam-archive-strategies.json';
import polygonArchiveStrategies from '@/configs/polygon-archive-strategies.json';
import polygonExperimentalStrategies from '@/configs/polygon-experimental-strategies.json';
import optimismBareStrategies from '@/configs/optimism-bare-strategies.json';
import polygonzkevmBareStrategies from '@/configs/polygonzkevm-bare-strategies.json';
import baseBareStrategies from '@/configs/base-bare-strategies.json';
import baseArchiveStrategies from '@/configs/base-archive-strategies.json';
import { AppType } from '@/core/types/AppType';
import { AppEnvironment } from '@/core/types/AppEnvironment';
import { APP_DOMAINS } from '@/core/constants/app-domains';
import { RestakeSupportedNetwork } from '@/core/types/RestakeSupportedNetwork';
import { Network } from '@/shared/types/Network';

export const appType = process.env.REACT_APP_ENTRY as AppType;

export const APPLY_LINK =
  'https://docs.google.com/forms/d/e/1FAIpQLScJXC08UK1A2b4-WydHpMsvdHn7fyW5mCdqlbZuiznCTN4D0A/viewform';
export const HOLESKY_RPC =
  'https://lb.drpc.org/ogrpc?network=holesky&dkey=As-DyyPNDUfnlRRhTL4jKLCWjjGHHRIR764CtigucSjy';

export const MAINNET_CHAIN_ID = 1;
export const POLYGON_CHAIN_ID = 137;

export const THRESHOLD_WSTETH = 0.001;
export const THRESHOLD_BASE_TOKEN = 1;

export type ChainId =
  | 1
  | 1101
  | 137
  | 1284
  | 42161
  | 10
  | 8453
  | 17000
  | 560048
  | 48900
  | 1135
  | 56
  | 252
  | 143
  | 10143
  | 9745
  | 30
  | 31612
  | 999;

export const NETWORKS: Record<ChainId, Network> = {
  1: 'ethereum',
  1101: 'polygonzkevm',
  137: 'polygon',
  1284: 'moonbeam',
  42161: 'arbitrum',
  10: 'optimism',
  8453: 'base',
  1135: 'lisk',
  252: 'fraxtal',
  17000: 'holesky',
  560048: 'hoodi',
  56: 'bsc',
  // 9889: 'fuel',
  48900: 'zircuit',
  143: 'monad',
  10143: 'monad-testnet',
  9745: 'plasma',
  30: 'rootstock',
  31612: 'mezo',
  999: 'hyperevm',
};

export const isTestNetwork = (network: Network) => network === 'holesky';

export const NETWORK_CHAIN = Object.fromEntries(
  Object.entries(NETWORKS).map(([key, value]) => [value, Number(key)]),
) as Record<Network, ChainId>;

export const NETWORKS_LIST = Object.values(NETWORKS);

export const isProduction = process.env.MODE === 'prod';
export const isStaging = process.env.MODE === 'stage';
export const isDev = process.env.NODE_ENV === 'development';
export const appEnvironment: AppEnvironment = (() => {
  if (isProduction) {
    return 'prod';
  }
  if (isStaging) {
    return 'staging';
  }
  return 'dev';
})();

export const CURRENT_APP_DOMAIN: string = APP_DOMAINS[appType][appEnvironment];

export const INITIAL_STRATEGIES = [
  ...ethereumBareStrategies,
  ...ethereumArchiveStrategies,
  ...polygonBareStrategies,
  ...polygonArchiveStrategies,
  ...polygonExperimentalStrategies,
  ...arbitrumArchiveStrategies,
  ...moonbeamArchiveStrategies,
  ...optimismBareStrategies,
  ...polygonzkevmBareStrategies,
  ...baseBareStrategies,
  ...baseArchiveStrategies,
].filter(strategy => (isProduction ? !strategy.id.includes('stage') : true));

export const TOKENS = new Set(INITIAL_STRATEGIES.map(strategy => strategy.tokens).flat());

export const SUPPORTED_CHAIN_IDS = INITIAL_STRATEGIES.reduce((networks, curStrategy) => {
  networks.add(NETWORK_CHAIN[curStrategy.network as Network]);
  return networks;
}, new Set<ChainId>());

export type RestakeInteropNetwork = 'lisk' | 'fraxtal' | 'bsc';

export const RESTAKE_SUPPORTED_CHAIN_ID_BY_NETWORK = new Map<Network, ChainId>(
  isProduction
    ? [
        ['ethereum', 1],
        ['bsc', 56],
        ['lisk', 1135],
        ['fraxtal', 252],
        ['monad', 143],
        ['base', 8453],
        ['plasma', 9745],
        ['arbitrum', 42161],
        ['rootstock', 30],
        ['mezo', 31612],
      ]
    : [
        ['ethereum', 1],
        ['bsc', 56],
        ['lisk', 1135],
        ['fraxtal', 252],
        ['holesky', 17000],
        ['hoodi', 560048],
        ['base', 8453],
        ['monad', 143],
        ['monad-testnet', 10143],
        ['plasma', 9745],
        ['arbitrum', 42161],
        ['rootstock', 30],
        ['mezo', 31612],
      ],
);
export const RESTAKE_SUPPORTED_CHAIN_IDS = new Set(RESTAKE_SUPPORTED_CHAIN_ID_BY_NETWORK.values());
export const RESTAKE_SUPPORTED_NETWORKS = Array.from(
  RESTAKE_SUPPORTED_CHAIN_ID_BY_NETWORK.keys(),
) as RestakeSupportedNetwork[];

export const RESTAKE_INTEROP_NETWORKS: Network[] = ['lisk', 'fraxtal', 'bsc', 'base'];

export const isRestakeInteropNetwork = (network?: Network): network is RestakeInteropNetwork =>
  network != null && RESTAKE_INTEROP_NETWORKS.includes(network);

export function isRestakeSupportedNetwork(
  network?: Network,
): network is Exclude<RestakeSupportedNetwork, RestakeInteropNetwork> {
  return network != null && RESTAKE_SUPPORTED_NETWORKS.includes(network as RestakeSupportedNetwork);
}

export const ETHERSCAN: Partial<Record<Network, string>> = {
  ethereum: 'https://etherscan.io',
  polygon: 'https://polygonscan.com',
  optimism: 'https://optimistic.etherscan.io',
  moonbeam: 'https://moonscan.io',
  arbitrum: 'https://arbiscan.io',
  polygonzkevm: 'https://zkevm.polygonscan.com',
  base: 'https://basescan.org',
  holesky: 'https://holesky.etherscan.io',
  unknown: 'https://etherscan.io',
  fuel: 'https://app.fuel.network',
  zircuit: 'https://explorer.zircuit.com',
  fraxtal: 'https://fraxscan.com',
  bsc: 'https://bscscan.com',
  hoodi: 'https://hoodi.etherscan.io',
};

export const BLOCKSCOUT: Partial<Record<Network, string>> = {
  ethereum: 'https://eth.blockscout.com',
  lisk: 'https://blockscout.lisk.com',
  holesky: 'https://eth-holesky.blockscout.com',
  hoodi: 'https://eth-hoodi.blockscout.com',
  base: 'https://base.blockscout.com',
  rootstock: 'https://rootstock.blockscout.com',
};

export const BLOCKVISION: Partial<Record<Network, string>> = {
  monad: 'https://monadvision.com',
};

export const EXPLORERS: Record<Network, { href: string; name: string }> = {
  ethereum: {
    href: 'https://eth.blockscout.com',
    name: 'Blockscout',
  },
  polygon: {
    href: 'https://polygonscan.com',
    name: 'Polygonscan',
  },
  optimism: {
    href: 'https://optimistic.etherscan.io',
    name: 'Optimistic Etherscan',
  },
  moonbeam: {
    href: 'https://moonscan.io',
    name: 'Moonscan',
  },
  arbitrum: {
    href: 'https://arbiscan.io',
    name: 'Arbiscan',
  },
  polygonzkevm: {
    href: 'https://zkevm.polygonscan.com',
    name: 'Polygonscan',
  },
  holesky: {
    href: 'https://eth-holesky.blockscout.com',
    name: 'Blockscout',
  },
  hoodi: {
    href: 'https://eth-hoodi.blockscout.com',
    name: 'Blockscout',
  },
  unknown: {
    href: 'https://etherscan.io',
    name: 'Etherscan',
  },
  fuel: {
    href: 'https://app.fuel.network',
    name: 'Fuel Explorer',
  },
  zircuit: {
    href: 'https://explorer.zircuit.com',
    name: 'Zircuit Explorer',
  },
  fraxtal: {
    href: 'https://fraxscan.com',
    name: 'Fraxscan',
  },
  bsc: {
    href: 'https://bscscan.com',
    name: 'Bscscan',
  },
  lisk: {
    href: 'https://blockscout.lisk.com',
    name: 'Blockscout',
  },
  base: {
    href: 'https://base.blockscout.com',
    name: 'Blockscout',
  },
  plasma: {
    href: 'https://plasmascan.to',
    name: 'PlasmaScan',
  },
  monad: {
    href: 'https://monadvision.com',
    name: 'MonVision',
  },
  'monad-testnet': {
    href: 'https://testnet.monvision.io',
    name: 'MonVision',
  },
  rootstock: {
    href: 'https://rootstock.blockscout.com',
    name: 'Blockscout',
  },
  mezo: {
    href: 'https://explorer.mezo.org',
    name: 'Mezo Explorer',
  },
  hyperevm: {
    href: 'https://hyperevmscan.io/',
    name: 'HyperEVM Scan',
  },
};

export function getExplorerAddressUrl(
  network: Network,
  address: string,
): {
  href: string;
} {
  if (network in BLOCKSCOUT) {
    return {
      href: `${BLOCKSCOUT[network]}/address/${address}`,
    } as const;
  }

  if (network in ETHERSCAN) {
    return {
      href: `${ETHERSCAN[network]}/address/${address}`,
    } as const;
  }

  if (network in EXPLORERS) {
    return {
      href: `${EXPLORERS[network].href}/address/${address}`,
    } as const;
  }

  return {
    href: address,
  } as const;
}

export function getExplorerTxUrl(
  network: Network,
  txHash: string,
): {
  href: string;
} {
  if (network in BLOCKSCOUT) {
    return {
      href: `${BLOCKSCOUT[network]}/tx/${txHash}`,
    } as const;
  }

  if (network in ETHERSCAN) {
    return {
      href: `${ETHERSCAN[network]}/tx/${txHash}`,
    } as const;
  }

  if (network in EXPLORERS) {
    return {
      href: `${EXPLORERS[network].href}/tx/${txHash}`,
    } as const;
  }

  return {
    href: txHash,
  } as const;
}

// @todo remove! defined but never used
export const TOKEN_ADDRESS: Record<Network, Record<string, string>> = {
  ethereum: {
    usdc: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48',
    lido: '0x5A98FcBEA516Cf06857215779Fd812CA3beF1B32',
  },
  optimism: {},
  unknown: {},
  polygon: {},
  moonbeam: {},
  arbitrum: {},
  polygonzkevm: {},
  holesky: {},
  hoodi: {},
  base: {},
  lisk: {},
  fraxtal: {},
  fuel: {},
  zircuit: {},
  bsc: {},
  plasma: {},
  monad: {},
  'monad-testnet': {},
  rootstock: {},
  mezo: {},
  hyperevm: {},
};

export const PROTOCOLS = new Set(
  INITIAL_STRATEGIES.map(strategy => strategy.protocols)
    .flat()
    .filter((value): value is string => !!value),
);

export const rpc: Record<Network, { url: string; isAvailable: boolean }[]> = {
  ethereum: [
    { url: 'https://rpc.mevblocker.io', isAvailable: true },
    { url: 'https://1rpc.io/eth', isAvailable: true },
    { url: 'https://eth.drpc.org', isAvailable: true },
    { url: 'https://mainnet.infura.io/v3/1e0764ca8992477d92a04fbdce548c97', isAvailable: true },
    {
      url: 'https://lb.drpc.org/ogrpc?network=ethereum&dkey=As-DyyPNDUfnlRRhTL4jKLBLpHdJJ1ER76DqhkHL9tz4',
      isAvailable: true,
    },
  ],
  polygon: [
    { url: 'https://1rpc.io/matic', isAvailable: true },
    { url: 'https://polygon-bor.publicnode.com', isAvailable: true },
    { url: 'https://endpoints.omniatech.io/v1/matic/mainnet/public', isAvailable: true },
    { url: 'https://polygon-bor.publicnode.com', isAvailable: true },
    { url: 'https://matic-mainnet-full-rpc.bwarelabs.com', isAvailable: true },
  ],
  moonbeam: [
    { url: 'https://rpc.ankr.com/moonbeam', isAvailable: true },
    { url: 'https://moonbeam.api.onfinality.io/public', isAvailable: true },
    { url: 'https://moonbeam.public.blastapi.io', isAvailable: true },
    { url: 'https://rpc.api.moonbeam.network', isAvailable: true },
    { url: 'https://1rpc.io/glmr', isAvailable: true },
  ],
  arbitrum: [
    { url: 'https://1rpc.io/arb', isAvailable: true },
    { url: 'https://arbitrum-one.public.blastapi.io', isAvailable: true },
    { url: 'https://arb-mainnet-public.unifra.io', isAvailable: true },
  ],
  optimism: [
    { url: 'https://endpoints.omniatech.io/v1/op/mainnet/public', isAvailable: true },
    { url: 'https://optimism.publicnode.com', isAvailable: true },
    { url: 'https://1rpc.io/op', isAvailable: true },
    { url: 'https://rpc.ankr.com/optimism', isAvailable: true },
    { url: 'https://optimism-mainnet.public.blastapi.io', isAvailable: true },
    { url: 'https://optimism-mainnet.infura.io', isAvailable: true },
  ],
  polygonzkevm: [
    { url: 'https://zkevm-rpc.com', isAvailable: true },
    { url: 'https://1rpc.io/zkevm', isAvailable: true },
  ],
  base: [
    { url: 'https://1rpc.io/base', isAvailable: true },
    { url: 'https://mainnet.base.org', isAvailable: true },
    { url: 'https://rpc.notadegen.com/base', isAvailable: true },
    { url: 'https://base.blockpi.network/v1/rpc/public', isAvailable: true },
    { url: 'https://base-mainnet.public.blastapi.io', isAvailable: true },
  ],
  lisk: [
    { url: 'https://lisk.drpc.org', isAvailable: true },
    { url: 'https://rpc.api.lisk.com', isAvailable: true },
    { url: 'https://lisk.gateway.tenderly.co', isAvailable: true },
  ],

  fraxtal: [
    { url: 'https://rpc.frax.com', isAvailable: true },
    { url: 'https://fraxtal.drpc.org', isAvailable: true },
  ],
  bsc: [
    { url: 'https://bsc-dataseed.bnbchain.org', isAvailable: true },
    { url: 'https://bsc-dataseed1.defibit.io', isAvailable: true },
    { url: 'https://bsc-dataseed1.ninicoin.io', isAvailable: true },
  ],
  holesky: [
    { url: HOLESKY_RPC, isAvailable: true },
    { url: 'https://ethereum-holesky.blockpi.network/v1/rpc/public', isAvailable: true },
    { url: 'https://ethereum-holesky-rpc.publicnode.com', isAvailable: true },
    { url: 'https://rpc-holesky.rockx.com', isAvailable: true },
    { url: 'https://endpoints.omniatech.io/v1/eth/holesky/public', isAvailable: true },
  ],
  hoodi: [
    { url: 'https://0xrpc.io/hoodi', isAvailable: true },
    { url: 'https://hoodi.drpc.org', isAvailable: true },
  ],
  unknown: [],
  fuel: [],
  zircuit: [],
  plasma: [
    { url: 'https://rpc.plasma.to', isAvailable: true },
    { url: 'https://plasma.drpc.org', isAvailable: true },
  ],
  monad: [
    {
      url: 'https://rpc-mainnet.monadinfra.com',
      isAvailable: true,
    },
    {
      url: 'https://rpc.monad.xyz',
      isAvailable: true,
    },
    {
      url: 'https://ancient-cosmopolitan-spree.monad-mainnet.quiknode.pro/05aa47124ffe7a8c881e5fa0edea51317f69814b',
      isAvailable: true,
    },
    {
      url: 'https://monad-mainnet.g.alchemy.com/v2/5tDwngSTNMm-SwG7xtUB4',
      isAvailable: true,
    },
  ],
  'monad-testnet': [
    {
      url: 'https://testnet-rpc.monad.xyz',
      isAvailable: true,
    },
    {
      url: 'https://monad-testnet.drpc.org',
      isAvailable: true,
    },
  ],
  rootstock: [
    { url: 'https://rootstock.drpc.org', isAvailable: true },
    { url: 'https://public-node.rsk.co', isAvailable: true },
  ],
  mezo: [
    {
      url: 'https://rpc-internal.mezo.org',
      isAvailable: true,
    },
    {
      url: 'https://mainnet.mezo.public.validationcloud.io',
      isAvailable: true,
    },
    {
      url: 'https://rpc-internal.mezo.org',
      isAvailable: true,
    },
    {
      url: 'https://rpc-http.mezo.boar.network',
      isAvailable: true,
    },
    {
      url: 'https://mezo.drpc.org',
      isAvailable: true,
    },
  ],
  hyperevm: [
    {
      url: 'https://hyperliquid.drpc.org',
      isAvailable: true,
    },
    {
      url: 'https://lb.drpc.live/hyperliquid/As-DyyPNDUfnlRRhTL4jKLBLpHdJJ1ER76DqhkHL9tz4',
      isAvailable: true,
    },
  ],
};

export const avgBlockTimeByNetwork: Record<Network, number> = {
  ethereum: 12.3,
  polygon: 2.3,
  arbitrum: 15,
  moonbeam: 6,
  optimism: 2,
  polygonzkevm: 1,
  bsc: 1,
  base: 6,
  lisk: 1,
  fraxtal: 2,
  holesky: 1,
  hoodi: 1,
  unknown: 0,
  fuel: 0,
  zircuit: 0,
  plasma: 1,
  monad: 0.4,
  'monad-testnet': 0.4,
  rootstock: 15,
  mezo: 16,
  hyperevm: 1, // 0.98
};

// @todo remove! defined but never used
export const networkSymbol: Record<Network, string> = {
  ethereum: 'ETH',
  polygon: 'MATIC',
  arbitrum: 'ETH',
  moonbeam: 'GLMR',
  optimism: 'ETH',
  polygonzkevm: 'ETH',
  base: 'ETH',
  lisk: 'LSK',
  fraxtal: 'FRAX',
  holesky: 'ETH',
  hoodi: 'ETH',
  bsc: 'BNB',
  unknown: 'ETH',
  fuel: 'ETH',
  zircuit: 'ETH',
  plasma: 'XPL',
  monad: 'MON',
  'monad-testnet': 'MON',
  rootstock: 'RBTC',
  mezo: '',
  hyperevm: '',
};

// @todo remove! defined but never used
export const networkRpcToSwitch: Record<Network, string> = {
  ethereum: 'https://mainnet.infura.io/v3/',
  polygon: 'https://polygon-rpc.com',
  arbitrum: 'https://arbitrum-mainnet.infura.io',
  moonbeam: 'https://rpc.api.moonbeam.network',
  optimism: 'https://optimism-mainnet.infura.io',
  polygonzkevm: 'https://zkevm-rpc.com',
  base: 'https://mainnet.base.org',
  lisk: 'https://lisk.drpc.org',
  fraxtal: 'https://rpc.frax.com',
  holesky: 'https://endpoints.omniatech.io/v1/eth/holesky/public',
  hoodi: 'https://hoodi.drpc.org',
  bsc: 'https://bsc-dataseed.bnbchain.org',
  unknown: '',
  fuel: '',
  zircuit: 'https://zircuit-mainnet.drpc.org',
  plasma: '',
  monad: 'https://rpc-mainnet.monadinfra.com',
  'monad-testnet': 'https://testnet-rpc.monad.xyz',
  rootstock: 'https://rootstock.drpc.org',
  mezo: '',
  hyperevm: '',
};

export const defillamaNetworkMap: Record<Network, string> = {
  ethereum: 'ethereum',
  polygon: 'polygon',
  arbitrum: 'arbitrum',
  moonbeam: 'moonbeam',
  optimism: 'optimism',
  polygonzkevm: 'polygon_zkevm',
  base: 'base',
  lisk: 'lisk',
  fraxtal: 'fraxtal',
  holesky: 'holesky',
  hoodi: 'hoodi',
  unknown: '',
  fuel: '',
  zircuit: 'zircuit',
  bsc: 'bsc',
  plasma: 'plasma',
  monad: 'monad',
  'monad-testnet': 'monad-testnet',
  rootstock: 'rootstock',
  mezo: 'mezo',
  hyperevm: 'hyperevm',
};

export const ZERO_BYTES =
  '0x0000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000';

export const VOID_USER: Record<Network, string> = {
  ethereum: '0x929C3dAF2E2Be4C74A56EC01dF374bc46A34C6A1',
  unknown: '0x929C3dAF2E2Be4C74A56EC01dF374bc46A34C6A1',
  polygon: '0x929C3dAF2E2Be4C74A56EC01dF374bc46A34C6A1',
  moonbeam: '0xFEED781Dff47cb04E33AFE475DF1d48092819684',
  arbitrum: '0x0000000000000000000000000000000000000000',
  optimism: '0x0000000000000000000000000000000000000000',
  base: '0x929C3dAF2E2Be4C74A56EC01dF374bc46A34C6A1',
  lisk: '0x0000000000000000000000000000000000000000',
  fraxtal: '0x0000000000000000000000000000000000000000',
  polygonzkevm: '0x929C3dAF2E2Be4C74A56EC01dF374bc46A34C6A1',
  holesky: '0x0000000000000000000000000000000000000000',
  hoodi: '0x0000000000000000000000000000000000000000',
  fuel: '0x0000000000000000000000000000000000000000',
  zircuit: '0x0000000000000000000000000000000000000000',
  bsc: '0x0000000000000000000000000000000000000000',
  plasma: '0x0000000000000000000000000000000000000000',
  monad: '0x0000000000000000000000000000000000000000',
  'monad-testnet': '0x0000000000000000000000000000000000000000',
  rootstock: '0x0000000000000000000000000000000000000000',
  mezo: '0x0000000000000000000000000000000000000000',
  hyperevm: '0x0000000000000000000000000000000000000000',
};

export const MOONBEAM_TOKEN_ORACLE = {
  USDC: '0xA122591F60115D63421f66F752EF9f6e0bc73abC',
  WGLMR: '0x4497B606be93e773bbA5eaCFCb2ac5E2214220Eb',
};
